import { useEffect, useMemo, useState } from 'react';
import { Card, Flex, Input, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { DeleteOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';

import AntDButton from 'src/components/Button';
import SharedDatePicker from 'src/components/DatePicker';
import { IAllProductResp } from 'src/models/AllProducts';
import ResourceSearchableDropdown from 'src/components/ResourceSearch';
import { ScrollableContent } from 'src/components/ScrollableContent';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';
import {
  assignProduct,
  fetchAllProducts,
} from 'src/redux/features/projects/projectsSlice';

const AddProduct = () => {
  const [dataSource, setDataSource] = useState<IAllProductResp[]>([]);
  const [value, setValue] = useState('');
  const [isProductTableLoading, setIsProductTableLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { Title } = Typography;
  const dispatch = useDispatch();

  //  Add new row
  const addRow = () => {
    if (value !== '') {
      setValue('');
    }
    const newRow: IAllProductResp = {
      id: dataSource.length + 1, // unique id
      isNew: true,
      name: '',
      startDate: dayjs().format('YYYY-MM-DD'),
      endDate: dayjs().add(1, 'year').month(2).date(31).format('YYYY-MM-DD'),
      owner: '',
      ownerName: '',
    };

    const updatedData = [...dataSource];

    updatedData.unshift(newRow);
    setDataSource(updatedData);
  };

  //  Handlers for inline editing
  const onProjectNameChange = (e, id) => {
    const productName = e.target.value;

    setDataSource((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, name: productName } : item,
      ),
    );
  };

  const onStartDateChange = (val, id) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              startDate: val ? dayjs(val).format('YYYY-MM-DD') : null,
            }
          : item,
      ),
    );
  };

  // End Date
  const onEndDateChange = (val, id) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, endDate: val ? dayjs(val).format('YYYY-MM-DD') : null }
          : item,
      ),
    );
  };

  // Owner
  const onOwnerChange = (val, id) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, owner: val?.value, ownerName: val?.label }
          : item,
      ),
    );
  };
  //  Duplicate check

  function identifyDuplicateNames(data) {
    const nameSet = new Set();
    const duplicates = [];

    for (const item of data) {
      const name = item.name?.trim().toLowerCase();

      if (name && nameSet.has(name)) {
        duplicates.push(name);
      } else if (name) {
        nameSet.add(name);
      }
    }

    return duplicates;
  }

  //  Remove row
  const removeRow = (id) => {
    const newData = dataSource.filter((item) => item.id !== id);

    setDataSource(newData);
  };
  const fetchData = async () => {
    setIsProductTableLoading(true);
    try {
      const data = await dispatch(fetchAllProducts()).unwrap();

      setDataSource(data);
    } catch (error) {
      console.error('Fetch failed:', error);
    } finally {
      setIsProductTableLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (value.trim() === '') return dataSource;

    return dataSource.filter((project) =>
      project.name?.toLowerCase().includes(value.toLowerCase()),
    );
  }, [value, dataSource]);

  useEffect(() => {
    fetchData();
  }, []);

  //  Table columns
  const columns = [
    {
      title: en.Projects.Title.productInitiative,
      dataIndex: 'name',
      key: 'name',
      render: (text, record, index) => (
        <Input
          title={text}
          // type="text"
          value={text}
          placeholder={en.Projects.Placeholder.enterProductInitiative}
          onChange={(e) => onProjectNameChange(e, record.id)}
          style={{
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        />
      ),
      width: '30%',
      ellipsis: true,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: en.Projects.Title.startDate,
      dataIndex: 'startDate',
      key: 'startDate',
      render: (value, record, index) => (
        <SharedDatePicker
          date={value ? dayjs(value).toDate() : null}
          onChange={(val) => onStartDateChange(val, record.id)}
        />
      ),
      width: '20%',
      sorter: (a, b) => dayjs(a.startDate).unix() - dayjs(b.startDate).unix(),
    },
    {
      title: en.Projects.Title.endDate,
      dataIndex: 'endDate',
      key: 'endDate',
      render: (value, record, index) => (
        <SharedDatePicker
          date={value ? dayjs(value).toDate() : null}
          onChange={(val) => onEndDateChange(val, record.id)}
        />
      ),
      width: '20%',
      sorter: (a, b) => dayjs(a.endDate).unix() - dayjs(b.endDate).unix(),
    },
    {
      title: en.Projects.Title.productOwner,
      dataIndex: 'ownerName',
      key: 'owner',
      render: (_, record) => (
        <ResourceSearchableDropdown
          selected={
            record.ownerName && record.owner
              ? { label: record.ownerName, value: record.owner }
              : null
          }
          onChange={(val) => onOwnerChange(val, record.id)}
          placeholder={en.Projects.Placeholder.selectProductOwner}
          internalResourceFlag={true}
        />
      ),
      width: '23%',
      sorter: (a, b) => (a.ownerName || '').localeCompare(b.ownerName || ''),
    },

    {
      title: '',
      key: 'action',
      render: (_, record) =>
        record?.isNew ? (
          <AntDButton
            type="link"
            prefixIcon={<DeleteOutlined />}
            onClick={() => removeRow(record.id)}
            htmlType="button"
          />
        ) : null,
    },
  ];

  const validateProjects = (): boolean => {
    if (!dataSource.every((item) => item.name?.trim() !== '')) {
      dispatch(setError(en.Projects.Error.enterProductName));

      return false;
    }

    if (!dataSource.every((item) => item.startDate && item.startDate !== '')) {
      dispatch(setError(en.Common.Error.enterStartDate));

      return false;
    }

    if (!dataSource.every((item) => item.endDate && item.endDate !== '')) {
      dispatch(setError(en.Common.Error.enterEndDate));

      return false;
    }

    if (
      !dataSource.every((item) =>
        dayjs(item.startDate).isSameOrBefore(dayjs(item.endDate)),
      )
    ) {
      dispatch(setError(en.Common.Error.startDateGreater));

      return false;
    }

    if (!dataSource.every((item) => !!item.owner)) {
      dispatch(setError(en.Projects.Error.selectProductOwner));

      return false;
    }

    const duplicateProjects = identifyDuplicateNames(dataSource);

    if (duplicateProjects.length > 0) {
      dispatch(setError(en.Projects.Error.productNameUnique));

      return false;
    }

    return true;
  };
  // Save handler
  const onSave = async () => {
    const isValidProjects = validateProjects();

    if (isValidProjects) {
      setIsSaving(true);
      const response = await dispatch(
        assignProduct({
          productProjects: dataSource.map((item) => ({
            id: item.isNew ? 0 : item.id,
            name: item.name,
            owner: item.owner,
            ownerName: item.ownerName,
            startDate: item.startDate,
            endDate: item.endDate,
          })),
        }),
      );

      setIsSaving(false);

      if (response.error) return;

      toast.success(en.Projects.Success.productSaved);
      setTimeout(() => window.location.reload(), 1500);
    }
  };

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {en.Projects.Title.editProductInitiatives}
      </Title>
      <Flex gap="middle" vertical>
        <Card size="small">
          <Flex justify="space-between" style={{ paddingBottom: '1rem' }}>
            <Input
              type="text"
              placeholder={en.Common.Placeholder.searchHere}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ width: '20rem' }}
              allowClear
            />
            <AntDButton
              onClick={addRow}
              type="primary"
              htmlType="button"
              statusOnComplete="Add"
            />
          </Flex>
          <ScrollableContent
            minHeight={'calc(100vh - 14.75rem)'}
            maxHeight={'calc(100vh - 14.75rem)'}
          >
            <Table
              dataSource={filteredProducts}
              columns={columns}
              rowKey={(record) => record.id}
              pagination={false}
              loading={isProductTableLoading}
              sticky
            />
          </ScrollableContent>
          <Flex justify="flex-end" style={{ paddingTop: '1rem' }}>
            {filteredProducts.length > 0 && (
              <AntDButton
                onClick={onSave}
                type="primary"
                htmlType="button"
                status="Saving..."
                statusOnComplete="Save"
                loading={isSaving}
              />
            )}
          </Flex>
        </Card>
      </Flex>
    </>
  );
};

export default AddProduct;
